// "Request upload" on an Opportunity or Processing file (Documents tab
// PR 6a, mockup v3 screen 4; docs/design/documents-tab-workflow.md).
//
// CONSUMER: internal-ui (DocumentsTab, record layout).
//
// Ask a teammate for documents, inside the OS: pick who, which documents
// (one drop box each), which shelf, and a note, then "Send to {name}".
// The teammate's bell rings and the request sits on their Tasks page until
// every box is filled or it expires. No Teams, no email.
//
// Who is pre-picked comes from the server (suggestRequestRecipient): the
// file's lender party when that lender is one of our own people, else the
// person this sender asked last, else nobody. Never a hardcoded name.
//
// The documents pre-filled are the file's MISSING checklist document items
// whose "how it's collected" is a request to someone other than the client
// (request_lender, request_insurance). Every other missing document item is
// offered as a "+ {name}" chip, and "+ Something else…" adds a free label.
// Items already waiting on an open request are left unticked, so a second
// request never asks twice by accident.
//
// DECISION: people outside Stewardship (a title officer, the client) are
// not recipients here. That half is PR 6b (a no-login link with the same
// named boxes and large files). Until then "Send the client a link
// instead" opens today's household upload link.

import { useEffect, useMemo, useState } from "react";

import {
  api,
  apiV11,
  ApiError,
  type AssignableUser,
  type DocumentRequestRec,
  type RecordChecklistKind,
  type SuggestedRecipientRec,
} from "../../api";
import { Modal } from "../Modal";
import { useToast } from "../Toast";

/** One box the request will ask for: a checklist item or a free label. */
interface DraftSlot {
  key: string;
  label: string;
  checklistTaskId: string | null;
}

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

function firstNameOf(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

// The Documents tab pre-ticks what the lender sends. Insurance's item (the
// CLUE report) has its own tab, so it is offered here but never pre-ticked.
/** A plain email address, or null (mirrors the server's check). */
function normalizeEmail(raw: string): string | null {
  const email = raw.trim().toLowerCase();
  return email.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : null;
}

/** Copy to the clipboard, quietly: the link also stays on screen with
 *  "Copy again", so a browser that refuses the clipboard loses nothing. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const TEAMMATE_REQUEST_VIA = new Set(["request_lender"]);

export type RequestTab = "documents" | "clue";

export interface RequestUploadModalProps {
  open: boolean;
  onClose: () => void;
  kind: RecordChecklistKind;
  recordId: string;
  /** Checklist item to pre-select (the drawer's "Request" on one item). */
  presetTaskId?: string | null;
  /** Called after a request is sent, so the tab can list it. */
  onSent?: () => void;
  /** Opens today's household upload link. Null hides the option (a file
   *  with no household). */
  onSendClientLink?: (() => void) | null;
  /** Which tab opens first ("Request CLUE report" opens on CLUE). */
  initialTab?: RequestTab;
}

export function RequestUploadModal({
  open,
  onClose,
  kind,
  recordId,
  presetTaskId = null,
  onSent,
  onSendClientLink = null,
  initialTab = "documents",
}: RequestUploadModalProps) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [users, setUsers] = useState<AssignableUser[]>([]);
  const [suggested, setSuggested] = useState<SuggestedRecipientRec | null>(null);
  const [recipientId, setRecipientId] = useState<string | null>(null);
  // Someone outside Stewardship (PR 6b): an email instead of a teammate,
  // and the link to copy once the request exists.
  const [outsiderEmail, setOutsiderEmail] = useState<string | null>(null);
  const [linkReady, setLinkReady] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");
  const [offered, setOffered] = useState<DraftSlot[]>([]);
  const [selected, setSelected] = useState<DraftSlot[]>([]);
  const [customOpen, setCustomOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [visibility, setVisibility] = useState<"internal" | "client">("internal");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // The CLUE tab (Documents tab PR 7, mockup v3 screen 5).
  const [tab, setTab] = useState<RequestTab>(initialTab);
  const [clueAssignee, setClueAssignee] = useState<{ user_id: string; name: string } | null>(null);
  const [recordAddress, setRecordAddress] = useState<string | null>(null);
  const [addressChoice, setAddressChoice] = useState<"record" | "other">("record");
  const [otherAddress, setOtherAddress] = useState("");
  const [party, setParty] = useState<"buyer" | "seller">("buyer");
  const [clueChecklistName, setClueChecklistName] = useState<string | null>(null);

  // Load everything the window needs each time it opens: the team, who to
  // suggest, the file's open requests and its checklist.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setSendError(null);
    setPicking(false);
    setSearch("");
    setCustomOpen(false);
    setCustomLabel("");
    setVisibility("internal");
    setNote("");
    setTab(initialTab);
    setOutsiderEmail(null);
    setLinkReady(null);
    setAddressChoice("record");
    setOtherAddress("");
    setParty("buyer");
    Promise.all([
      api.listAssignableUsers(),
      apiV11.listRecordRequests(kind, recordId),
      apiV11.getRecordChecklist(kind, recordId).catch(() => null),
    ])
      .then(([team, reqs, checklist]) => {
        if (cancelled) return;
        setUsers(team.users);
        setSuggested(reqs.suggested_recipient);
        const clue = reqs.clue ?? { assignee: null, property_address: null };
        setClueAssignee(clue.assignee);
        setRecordAddress(clue.property_address);
        const first = initialTab === "clue" ? clue.assignee?.user_id : reqs.suggested_recipient?.user_id;
        setRecipientId(first ?? null);
        setPicking(!first);
        const clueItem = (checklist?.checklists ?? []).find((c) =>
          c.items.some(
            (i) => i.item_type === "document" && i.state === "missing" && i.collect_via === "request_insurance",
          ),
        );
        setClueChecklistName(clueItem?.name ?? null);

        const alreadyAsked = new Set<string>();
        for (const r of reqs.requests as DocumentRequestRec[]) {
          for (const s of r.slots) {
            if (s.checklist_task_id && !s.document_id) alreadyAsked.add(s.checklist_task_id);
          }
        }
        const missing = (checklist?.checklists ?? [])
          .flatMap((c) => c.items)
          .filter((i) => i.item_type === "document" && i.state === "missing");
        const pre: DraftSlot[] = [];
        const rest: DraftSlot[] = [];
        for (const item of missing) {
          const slot = { key: `task:${item.id}`, label: item.title, checklistTaskId: item.id };
          const preselect =
            item.id === presetTaskId ||
            (!presetTaskId &&
              !alreadyAsked.has(item.id) &&
              TEAMMATE_REQUEST_VIA.has(item.collect_via ?? ""));
          (preselect ? pre : rest).push(slot);
        }
        setSelected(pre);
        setOffered(rest);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : "Could not load the request window.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, kind, recordId, presetTaskId]);

  const recipient = useMemo(
    () => users.find((u) => u.id === recipientId) ?? null,
    [users, recipientId],
  );
  const recipientName = recipient?.name ?? recipient?.email ?? null;

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? users.filter(
          (u) =>
            (u.name ?? "").toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
        )
      : users;
    return list.slice(0, 8);
  }, [users, search]);

  function toggleOff(slot: DraftSlot) {
    setSelected((prev) => prev.filter((s) => s.key !== slot.key));
    if (slot.checklistTaskId) setOffered((prev) => [...prev, slot]);
  }

  function toggleOn(slot: DraftSlot) {
    setOffered((prev) => prev.filter((s) => s.key !== slot.key));
    setSelected((prev) => [...prev, slot]);
  }

  function addCustom() {
    const label = customLabel.trim();
    if (!label) return;
    setSelected((prev) => [
      ...prev,
      { key: `free:${Date.now()}:${prev.length}`, label, checklistTaskId: null },
    ]);
    setCustomLabel("");
    setCustomOpen(false);
  }

  // Switching tabs switches who it goes to: the lender default for
  // documents, the Insurance default for CLUE.
  function switchTab(next: RequestTab) {
    if (next === tab) return;
    setTab(next);
    setSendError(null);
    setOutsiderEmail(null);
    const who = next === "clue" ? clueAssignee?.user_id : suggested?.user_id;
    setRecipientId(who ?? null);
    setPicking(!who);
    setSearch("");
  }

  const clueAddress = (
    kind === "transaction" && recordAddress && addressChoice === "record" ? recordAddress : otherAddress
  ).trim();

  async function send() {
    if (tab === "clue") {
      if (!recipientId || !clueAddress) return;
      setSending(true);
      setSendError(null);
      try {
        await apiV11.createRecordRequest(kind, recordId, {
          recipient_user_id: recipientId,
          slots: [],
          visibility,
          note: note.trim() || null,
          request_type: "clue",
          property_address: clueAddress,
          subject_party: party,
        });
        toast.success(`Sent to ${recipientName ?? "your teammate"}.`);
        onSent?.();
        onClose();
      } catch (err) {
        setSendError(err instanceof Error ? err.message : "Could not send the request.");
      } finally {
        setSending(false);
      }
      return;
    }
    if ((!recipientId && !outsiderEmail) || selected.length === 0) return;
    setSending(true);
    setSendError(null);
    if (outsiderEmail) {
      try {
        const made = await apiV11.createRecordRequest(kind, recordId, {
          recipient_email: outsiderEmail,
          slots: selected.map((s) =>
            s.checklistTaskId ? { checklist_task_id: s.checklistTaskId } : { label: s.label },
          ),
          visibility,
          note: note.trim() || null,
        });
        const url = `${window.location.origin}${made.link_path ?? ""}`;
        setLinkReady(url);
        await copyText(url);
        onSent?.();
      } catch (err) {
        setSendError(err instanceof Error ? err.message : "Could not make the link.");
      } finally {
        setSending(false);
      }
      return;
    }
    try {
      await apiV11.createRecordRequest(kind, recordId, {
        recipient_user_id: recipientId ?? "",
        slots: selected.map((s) =>
          s.checklistTaskId ? { checklist_task_id: s.checklistTaskId } : { label: s.label },
        ),
        visibility,
        note: note.trim() || null,
      });
      toast.success(`Sent to ${recipientName ?? "your teammate"}.`);
      onSent?.();
      onClose();
    } catch (err) {
      setSendError(
        err instanceof ApiError || err instanceof Error ? err.message : "Could not send the request.",
      );
    } finally {
      setSending(false);
    }
  }

  const first = outsiderEmail ? null : recipientName ? firstNameOf(recipientName) : null;
  const typedEmail = tab === "documents" ? normalizeEmail(search) : null;
  const sendDisabledReason = !recipientId && !outsiderEmail
    ? "Pick who to send it to."
    : tab === "clue"
      ? clueAddress
        ? null
        : "Add the property's address first."
      : selected.length === 0
        ? "Pick at least one document."
        : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={tab === "clue" ? "Request a CLUE report" : "Ask someone to drop a file here"}
      className="req-modal"
      data-testid="request-upload-modal"
      footer={
        linkReady ? (
          <div className="req-foot">
            <span />
            <span className="req-foot-r">
              <button type="button" className="doc-btn doc-btn--primary" onClick={onClose}>
                Done
              </button>
            </span>
          </div>
        ) : (
        <div className="req-foot">
          {tab === "clue" ? (
            <span className="req-hint req-foot-hint">
              {clueAddress
                ? `${first ?? "They"} get${first ? "s" : ""} a notification in the OS and drop${first ? "s" : ""} the report from it`
                : `Send to ${first ?? "them"} stays off until there's an address`}
            </span>
          ) : onSendClientLink ? (
            <button
              type="button"
              className="doc-link-btn"
              onClick={() => {
                onClose();
                onSendClientLink();
              }}
            >
              Send the client a link instead
            </button>
          ) : (
            <span />
          )}
          <span className="req-foot-r">
            <button type="button" className="doc-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="doc-btn doc-btn--primary"
              disabled={sending || loading || !!sendDisabledReason}
              title={sendDisabledReason ?? undefined}
              onClick={() => void send()}
            >
              {sending ? "Sending…" : outsiderEmail ? "Copy link" : first ? `Send to ${first}` : "Send"}
            </button>
          </span>
        </div>
        )
      }
    >
      <div className="req-eyebrow">Request</div>
      <div className="req-seg" role="tablist" aria-label="What to request">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "documents"}
          className={tab === "documents" ? "on" : undefined}
          onClick={() => switchTab("documents")}
        >
          Documents
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "clue"}
          className={tab === "clue" ? "on" : undefined}
          onClick={() => switchTab("clue")}
        >
          CLUE report
        </button>
      </div>
      {loading ? <p className="req-hint">Loading…</p> : null}
      {loadError ? (
        <p className="req-error" role="alert">
          {loadError}
        </p>
      ) : null}
      {linkReady ? (
        <div className="req-linkready" data-testid="request-link-ready">
          <p className="req-linkready-lede">
            <b>Link copied.</b> Paste it into your own email or text to {outsiderEmail}. The OS doesn't
            send it.
          </p>
          <div className="req-linkready-row">
            <input className="req-inp" readOnly value={linkReady} aria-label="The link" onFocus={(e) => e.target.select()} />
            <button type="button" className="doc-btn" onClick={() => void copyText(linkReady)}>
              Copy again
            </button>
          </div>
          <p className="req-hint">
            No account needed. One file per box, up to 150 MB each. Open for 14 days, or until every box is
            filled. It shows under "Requests out" on this file.
          </p>
        </div>
      ) : null}
      {!loading && !loadError && !linkReady ? (
        <>
          <div className="req-fld">
            <div className="req-lb">Send to</div>
            {outsiderEmail && !picking ? (
              <div className="req-person">
                <span className="req-av req-av--out" aria-hidden>
                  @
                </span>
                <span className="req-person-text">
                  <span className="req-nm">{outsiderEmail}</span>{" "}
                  <span className="req-rl">· Outside Stewardship</span>
                </span>
                <span className="req-dflt">Gets a link</span>
                <button
                  type="button"
                  className="doc-link-btn req-change"
                  onClick={() => {
                    setPicking(true);
                    setSearch(outsiderEmail);
                  }}
                >
                  Change
                </button>
              </div>
            ) : recipient && !picking ? (
              <div className="req-person">
                <span className="req-av" aria-hidden>
                  {initialsOf(recipientName ?? "?")}
                </span>
                <span className="req-person-text">
                  <span className="req-nm">{recipientName}</span>{" "}
                  <span className="req-rl">· {recipient.email}</span>
                </span>
                {tab === "clue" && clueAssignee?.user_id === recipient.id ? (
                  <span className="req-dflt req-dflt--g">Default</span>
                ) : tab === "documents" && suggested && suggested.user_id === recipient.id ? (
                  <span className="req-dflt">
                    {suggested.reason === "lender" ? "Record's lender" : "Last asked"}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="doc-link-btn req-change"
                  onClick={() => {
                    setPicking(true);
                    setSearch("");
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="req-picker">
                <input
                  className="req-inp"
                  type="search"
                  aria-label="Search the team"
                  placeholder={
                    tab === "documents"
                      ? "Search the team, or type an email for someone outside"
                      : "Search the team by name or email"
                  }
                  value={search}
                  autoFocus
                  onChange={(e) => setSearch(e.target.value)}
                />
                <ul className="req-picklist" role="listbox" aria-label="Teammates">
                  {matches.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={u.id === recipientId}
                        className="req-pickrow"
                        onClick={() => {
                          setRecipientId(u.id);
                          setOutsiderEmail(null);
                          setPicking(false);
                        }}
                      >
                        <span className="req-av" aria-hidden>
                          {initialsOf(u.name ?? u.email)}
                        </span>
                        <span className="req-person-text">
                          <span className="req-nm">{u.name ?? u.email}</span>{" "}
                          <span className="req-rl">· {u.email}</span>
                        </span>
                      </button>
                    </li>
                  ))}
                  {typedEmail && !users.some((u) => u.email.toLowerCase() === typedEmail) ? (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected={outsiderEmail === typedEmail}
                        className="req-pickrow"
                        onClick={() => {
                          setOutsiderEmail(typedEmail);
                          setRecipientId(null);
                          setPicking(false);
                        }}
                      >
                        <span className="req-av req-av--out" aria-hidden>
                          @
                        </span>
                        <span className="req-person-text">
                          <span className="req-nm">Send a link to {typedEmail}</span>{" "}
                          <span className="req-rl">· someone outside Stewardship</span>
                        </span>
                      </button>
                    </li>
                  ) : null}
                  {matches.length === 0 && !typedEmail ? (
                    <li className="req-hint">No one matches that. For someone outside Stewardship, type their email.</li>
                  ) : null}
                </ul>
              </div>
            )}
            <div className="req-hint">
              {tab === "clue"
                ? "Whoever pulls CLUE reports for Insurance. Search to ask someone else."
                : outsiderEmail
                  ? "Not a teammate: they upload through a link, never see the OS, and never become a contact."
                  : suggested?.reason === "lender" && recipientId === suggested.user_id
                    ? "The record's lender. Search for someone else at Stewardship, or type an email for someone outside."
                    : "Anyone at Stewardship. They get a notification in the OS, not an email. For someone outside, type their email."}
            </div>
          </div>

          {tab === "clue" ? (
            <>
              {kind === "transaction" && recordAddress ? (
                <div className="req-fld" role="radiogroup" aria-label="Property">
                  <div className="req-lb">Property</div>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={addressChoice === "record"}
                    className={`req-radio${addressChoice === "record" ? " on" : ""}`}
                    onClick={() => setAddressChoice("record")}
                  >
                    <span className="req-rb" aria-hidden />
                    <span>
                      <span className="req-radio-nm">{recordAddress}</span>
                      <span className="req-radio-mt">This record's property · filled in automatically</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={addressChoice === "other"}
                    className={`req-radio${addressChoice === "other" ? " on" : ""}`}
                    onClick={() => setAddressChoice("other")}
                  >
                    <span className="req-rb" aria-hidden />
                    <span className="req-radio-nm">Another address</span>
                  </button>
                  {addressChoice === "other" ? (
                    <input
                      className="req-inp"
                      aria-label="Property address"
                      placeholder="Street address, city"
                      value={otherAddress}
                      maxLength={200}
                      autoFocus
                      onChange={(e) => setOtherAddress(e.target.value)}
                    />
                  ) : null}
                </div>
              ) : (
                <label className="req-fld req-fld--block">
                  <span className="req-lb">Property address</span>
                  <input
                    className="req-inp"
                    placeholder="Street address, city"
                    value={otherAddress}
                    maxLength={200}
                    onChange={(e) => setOtherAddress(e.target.value)}
                  />
                </label>
              )}
              <div className="req-fld">
                <div className="req-lb">Loss history for the</div>
                <div className="req-seg req-seg--flush" role="radiogroup" aria-label="Loss history for the">
                  {(["buyer", "seller"] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      role="radio"
                      aria-checked={party === p}
                      className={party === p ? "on" : undefined}
                      onClick={() => setParty(p)}
                    >
                      {p === "buyer" ? "Buyer" : "Seller"}
                    </button>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {tab === "documents" ? (
          <div className="req-fld">
            <div className="req-lb">Documents requested</div>
            <div className="req-chips">
              {selected.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="req-chip on"
                  aria-label={`Remove ${s.label}`}
                  onClick={() => toggleOff(s)}
                >
                  {s.label} ✕
                </button>
              ))}
              {offered.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className="req-chip add"
                  onClick={() => toggleOn(s)}
                >
                  + {s.label}
                </button>
              ))}
              {customOpen ? (
                <span className="req-custom">
                  <input
                    className="req-inp"
                    aria-label="Name the document"
                    placeholder="Name the document"
                    value={customLabel}
                    maxLength={120}
                    autoFocus
                    onChange={(e) => setCustomLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCustom();
                      }
                    }}
                  />
                  <button type="button" className="doc-btn" onClick={addCustom}>
                    Add
                  </button>
                </span>
              ) : (
                <button type="button" className="req-chip add" onClick={() => setCustomOpen(true)}>
                  + Something else…
                </button>
              )}
            </div>
            <div className="req-hint">
              Pulled from this record's checklist. One drop box per document.
            </div>
          </div>
          ) : null}

          <div className="req-fld">
            <div className="req-lb">Saves to</div>
            <div className="req-two" role="radiogroup" aria-label="Saves to">
              <button
                type="button"
                role="radio"
                aria-checked={visibility === "internal"}
                className={`req-opt${visibility === "internal" ? " on" : ""}`}
                onClick={() => setVisibility("internal")}
              >
                Internal &amp; audit only
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={visibility === "client"}
                className={`req-opt${visibility === "client" ? " on" : ""}`}
                onClick={() => setVisibility("client")}
              >
                Visible to client
              </button>
            </div>
            {tab === "clue" ? (
              <div className="req-hint">
                {clueChecklistName ? (
                  <>
                    Checks off <b>CLUE report</b> on the {clueChecklistName} checklist
                  </>
                ) : kind === "opportunity" ? (
                  "Filed on this opportunity, labeled with the address"
                ) : (
                  "Filed on this record, labeled with the address"
                )}
              </div>
            ) : null}
          </div>

          <label className="req-fld req-fld--block">
            <span className="req-lb">Note</span>
            <textarea
              className="req-inp req-ta"
              value={note}
              maxLength={1000}
              placeholder={first ? `A line for ${first} (optional)` : "A line for them (optional)"}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>

          {tab === "documents" && outsiderEmail ? (
            <div className="req-hint req-hint--gap">
              They get a no-login link that opens only these boxes · you copy it into your own email or
              text · checks off the checklist item on upload · you're notified when a file lands · open
              for 14 days
            </div>
          ) : null}
          {tab === "documents" && !outsiderEmail ? (
          <div className="req-hint req-hint--gap">
            {first ?? "They"} get{first ? "s" : ""} a notification in the OS and drop
            {first ? "s" : ""} the files from it · checks off the checklist item on upload ·
            you're notified when a file lands · open for 14 days
          </div>
          ) : null}
          {sendError ? (
            <p className="req-error" role="alert">
              {sendError}
            </p>
          ) : null}
        </>
      ) : null}
    </Modal>
  );
}
